import { useEffect, useRef, useState } from "react";
import { BsChevronDown, BsChevronRight, BsFillVolumeMuteFill, BsFillVolumeUpFill } from "react-icons/bs";
import { CgArrowsExpandUpRight } from "react-icons/cg";
import { TbArrowDownCircle, TbArrowRightCircle } from "react-icons/tb";

interface SplitCardItems {
    title: JSX.Element | string;
    subtitle: JSX.Element | string;
    disableExpand?: boolean;
    onExpand?: () => void;
}

interface SplitCardSliderProps {
    id: string;
    rightPaneTitle: string;
    rightPaneSubtitle: string;
    rightPanLearnMoreAction?: () => void;
    leftPaneMediaType: "image" | "video";
    leftPaneMediaUrl: string;
    mediaPosterUrl?: string;
    onMediaLoaded?: () => void;
    items: SplitCardItems[];
    cardsScrollHeight?: number;
    textContentStyle?: React.CSSProperties;
    footerContents?: JSX.Element;
    onNavigate?: () => void;
}

export function NeoSplitSliderCardsView(props: SplitCardSliderProps) {
    const [viewWidth, setViewWidth] = useState<number>(window.innerWidth);
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const [isVideoMuted, setIsVideoMuted] = useState(false);
    const [videoHasFinished, setVideoHasFinished] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const cardsContainerRef = useRef<HTMLDivElement>(null);
    // Scroll direcion for about containers, -1 for up, 1 for down
    const [containerScrollPosition, setContainerScrollPosition] = useState<"top" | "bottom">("top");

    const isMobile = viewWidth <= 500;

    useEffect(() => {
        window.addEventListener("resize", () => setViewWidth(window.innerWidth));

        /**
         * Listen to scroll changes in the about container cards and display the top or bottom arrow keys
         */
        const sectionContainer = cardsContainerRef.current as unknown as HTMLDivElement;

        sectionContainer.addEventListener("scroll", (e) => {
            const scroll_bottom_index = sectionContainer.scrollHeight - sectionContainer.offsetHeight;

            if (sectionContainer.scrollTop === 0) {
                setContainerScrollPosition("top");
            } else if (Math.round(sectionContainer.scrollTop) === Math.round(scroll_bottom_index)) {
                setContainerScrollPosition("bottom");
            }

            // hide last action on mobile
            const last_action_btn = document.getElementById("last-about-action-btn") as HTMLElement;

            if (sectionContainer.scrollLeft / sectionContainer.scrollWidth >= 0.8876064852981589) {
                last_action_btn.style.display = "none";
            } else {
                last_action_btn.style.display = "block";
            }
        });

        return () => {
            window.removeEventListener("resize", () => setViewWidth(window.innerWidth));
        };
    }, []);

    const videoEnded = () => {
        setVideoHasFinished(true);
        setIsVideoPlaying(false);
        videoRef.current?.load();
    };

    const changeMuteStatus = () => {
        let video: HTMLVideoElement = videoRef.current as HTMLVideoElement;

        if (isVideoMuted) {
            video.muted = false;
            setIsVideoMuted(false);
        } else {
            video.muted = true;
            setIsVideoMuted(true);
        }
    };

    const scrollVerticalInAboutContainer = (direction: -1 | 1) => {
        const container = cardsContainerRef.current as unknown as HTMLElement;
        let scroll_offset = 300;

        switch (direction) {
            case 1:
            default:
                /**
                 * This value hold the: {element}.scrollTop value for the bottom.
                 */
                const scroll_bottom_index = container.scrollHeight - container.offsetHeight;
                /**
                 *  While the user scrolls down the page, we want to detect when he is the bottom at least close to the bottom,
                 *  In that case we just scroll the user to the bottom of the container.
                 */
                if (container.scrollTop + scroll_offset >= scroll_bottom_index) {
                    container.scroll({ top: scroll_bottom_index, left: 0, behavior: "smooth" });
                    setContainerScrollPosition("bottom");
                } else {
                    container.scroll({ top: container.scrollTop + scroll_offset, left: 0, behavior: "smooth" });
                }
                break;
            case -1:
                if (container.scrollTop <= scroll_offset) {
                    setContainerScrollPosition("top");
                    container.scroll({ top: 0, left: 0, behavior: "smooth" });
                } else {
                    container.scroll({ top: container.scrollTop - scroll_offset, left: 0, behavior: "smooth" });
                }
                break;
        }

        // setAboutCardsTranslateValue(scroll_index)
    };

    const scrollHorizontalInAboutContainer = (direction: -1 | 1) => {
        const container = cardsContainerRef.current as unknown as HTMLElement;
        let scroll_offset = document.body.clientWidth - 30;

        switch (direction) {
            case 1:
            default:
                /**
                 * This value hold the: {element}.scrollTop value for the bottom.
                 */
                const scroll_right_index = container.scrollWidth - container.offsetWidth;
                // last action btn on mobile

                /**
                 *  While the user scrolls down the page, we want to detect when he is the bottom at least close to the bottom,
                 *  In that case we just scroll the user to the bottom of the container.
                 */
                if (container.scrollLeft + scroll_offset + 10 >= scroll_right_index) {
                    container.scroll({ top: 0, left: scroll_right_index, behavior: "smooth" });
                    setContainerScrollPosition("bottom");
                } else {
                    container.scroll({ top: 0, left: container.scrollLeft + scroll_offset, behavior: "smooth" });
                }

                break;
            case -1:
                if (container.scrollTop <= scroll_offset) {
                    setContainerScrollPosition("top");
                    container.scroll({ top: 0, left: 0, behavior: "smooth" });
                } else {
                    container.scroll({ top: 0, left: container.scrollTop - scroll_offset, behavior: "smooth" });
                }
                break;
        }
    };

    const onNavigatePage = () => {
        if (typeof props.onNavigate === "function") {
            props.onNavigate();
        }
    };

    const onLearnMoreAction = () => {
        if (typeof props.rightPanLearnMoreAction === "function") {
            props.rightPanLearnMoreAction();
        }
    };

    const onMediaLoaded = () => {
        if (typeof props.onMediaLoaded === "function") {
            props.onMediaLoaded();
        }
    };

    return (
        <section id={props.id} className="overview-panel shared-panel">
            {props.leftPaneMediaType === "video" && (
                <div className="img-container video-container">
                    {isMobile ? (
                        <video onLoad={onMediaLoaded} ref={videoRef} id="about-video" poster={props.mediaPosterUrl} autoPlay={false} controls={false} playsInline={true} preload="metadata" loop={false} onEnded={videoEnded}>
                            <source src={props.leftPaneMediaUrl} type="video/mp4" />
                        </video>
                    ) : (
                        <video onLoad={onMediaLoaded} ref={videoRef} id="about-video" poster={props.mediaPosterUrl} autoPlay={false} controls={false} playsInline={true} preload="metadata" loop={false} onEnded={videoEnded}>
                            <source src={props.leftPaneMediaUrl} type="video/mp4" />
                        </video>
                    )}
                    {!videoHasFinished && (
                        <div className="actions">
                            <button onClick={changeMuteStatus} className="action-btn">
                                {isVideoMuted ? <BsFillVolumeMuteFill className="icon" /> : <BsFillVolumeUpFill className="icon" />}
                            </button>
                        </div>
                    )}
                </div>
            )}

            {props.leftPaneMediaType === "image" && (
                <div className="img-container">
                    {/*<Skeleton style={{zIndex: 9999 }} className="w-full h-full" />*/}
                    <img className="banner-image" src={props.leftPaneMediaUrl} onLoad={onMediaLoaded} />
                    <div className="img-container-footer">
                        <img src="/ATS_Global_Branding_Icon_White.png" alt="" className="h-[75px]" />
                    </div>
                </div>
            )}

            <div className="text-container flex-col">
                <div className="text-contents about" style={props.textContentStyle || {}}>
                    <div className="header">
                        <h1 className="title">{props.rightPaneTitle}</h1>
                        <h2 className="subtitle">{props.rightPaneSubtitle}</h2>
                    </div>
                </div>
                <div className="container-action">
                    {containerScrollPosition === "bottom" && (
                        <button onClick={() => scrollVerticalInAboutContainer(-1)} className="container-action-btn">
                            <TbArrowDownCircle className="icon up-icon" />
                            {/*<span className="text">next</span>*/}
                        </button>
                    )}

                    {containerScrollPosition === "top" && (
                        <button onClick={() => scrollVerticalInAboutContainer(1)} className="container-action-btn">
                            <TbArrowDownCircle className="icon" />
                            {/*<span className="text">next</span>*/}
                        </button>
                    )}
                </div>

                <div ref={cardsContainerRef} className="container-cards">
                    {props.items.map((item, index) => {
                        const isLastItem = index === props.items.length - 1;
                        return (
                            <SplitCardListItem
                                key={"item" + index}
                                title={item.title}
                                isLastItem={isLastItem}
                                subtitle={item.subtitle}
                                onExpand={item.onExpand}
                                onNavigateRight={() => scrollHorizontalInAboutContainer(1)}
                            />
                        );
                    })}

                    <div className="learn-more card-item-row mt-10">
                        <div className="left-col no-icon"></div>
                        <div className="learn-more-card text-center">
                            {!!props.rightPanLearnMoreAction && (
                                <button onClick={onLearnMoreAction} className="action-btn">
                                    Learn More.
                                </button>
                            )}
                            <div className="feature-btn-section">
                                <button className="btn" onClick={onNavigatePage}>
                                    <BsChevronDown className="icon" />
                                </button>
                            </div>

                            <div className="mt-3">
                                <button className="feature-btn" onClick={onNavigatePage}>
                                    Next
                                    <TbArrowRightCircle className="icon" />
                                </button>
                            </div>
                        </div>
                        <div className="right-col"></div>
                    </div>
                    {props.footerContents && <div className="overview-footer mt-8">{props.footerContents}</div>}
                </div>
            </div>
        </section>
    );
}

interface SplitCardListItemProps extends SplitCardItems {
    onExpand?: () => void;
    isLastItem?: boolean;
    disableExpand?: boolean;
    onNavigateRight: () => void;
}

export function SplitCardListItem({ title, subtitle, onExpand, isLastItem, disableExpand, onNavigateRight }: SplitCardListItemProps) {
    const onExpandCard = () => {
        if (typeof onExpand === "function") {
            onExpand();
        }
    };

    const onNavigateRightClick = () => {
        if (typeof onNavigateRight === "function") {
            onNavigateRight();
        }
    };

    return (
        <div className="card-item-row">
            {(!disableExpand && typeof onExpand === "function") ? (<div className="left-col">
                 
                    <button onClick={onExpandCard}>
                        <CgArrowsExpandUpRight className="icon" />
                    </button>
               
            </div> ) : (<div className=""></div>)}
            <div className="w-full">
                <div className="card-item">
                    <h1 className="title">{title}</h1>
                    <p className="description">{subtitle}</p>
                </div>
            </div>
            <div className="right-col">
                <div className="feature-btn-section">
                    <button id={isLastItem ? "last-about-action-btn" : ""} className="btn" onClick={onNavigateRightClick}>
                        <BsChevronRight className="icon" />
                    </button>
                </div>
            </div>
        </div>
    );
}
